import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Loader, EmptyState } from "@/components/common/Bits";
import { StatusBadge } from "@/components/common/StatusBadge";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

import { Calendar } from "@/components/ui/calendar";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { CalendarClock, Plus, Users, CalendarIcon } from "lucide-react";

import { toast } from "sonner";

const ACTIONS = {
  pending: [
    {
      status: "confirmed",
      label: "Confirm",
    },
    {
      status: "cancelled",
      label: "Cancel",
    },
  ],

  confirmed: [
    {
      status: "seated",
      label: "Mark Seated",
    },
    {
      status: "cancelled",
      label: "Cancel",
    },
    {
      status: "no_show",
      label: "No Show",
    },
  ],

  seated: [
    {
      status: "completed",
      label: "Complete",
    },
  ],
};
const isTableSelectableForReservation = (
  table,
  reservationDate,
  reservationTime,
) => {
  if (!table) return false;

  // Physically available table is always selectable
  if (table.status === "available") {
    return true;
  }

  // Occupied/billing table can be reserved only if
  // reservation is at least 30 minutes in the future.
  if (table.status !== "occupied" && table.status !== "billing") {
    return false;
  }

  if (!reservationDate || !reservationTime) {
    return false;
  }

  const [year, month, day] = reservationDate.split("-").map(Number);

  const [hours, minutes] = reservationTime.split(":").map(Number);

  const reservationDateTime = new Date(
    year,
    month - 1,
    day,
    hours,
    minutes,
    0,
    0,
  );

  const now = new Date();

  const diffMs = reservationDateTime.getTime() - now.getTime();

  const thirtyMinutes = 30 * 60 * 1000;

  return diffMs >= thirtyMinutes;
};
const getToday = () => {
  const today = new Date();

  const year = today.getFullYear();

  const month = String(today.getMonth() + 1).padStart(2, "0");

  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatReservationTime = (time) => {
  if (!time) return "";

  const [hours, minutes] = time.split(":").map(Number);

  const date = new Date();
  date.setHours(hours, minutes, 0, 0);

  return date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export default function Reservations() {
  const { branchId, branches } = useApp();

  const activeBranch = branchId === "all" ? branches[0]?.id : branchId;

  const [list, setList] = useState(null);

  const [tables, setTables] = useState([]);

  const [open, setOpen] = useState(false);

  const [dateOpen, setDateOpen] = useState(false);

  const [filterDate, setFilterDate] = useState(getToday());

  // ---------------------------------------------------------
  // Common confirmation dialog state
  // ---------------------------------------------------------
  const [confirmReservation, setConfirmReservation] = useState(null);

  const [confirmLoading, setConfirmLoading] = useState(false);

  const [form, setForm] = useState({
    customer_name: "",
    phone: "",
    date: getToday(),
    time: "20:00",
    guests: 2,
    table_id: "",
    special_request: "",
  });

  // ---------------------------------------------------------
  // Load reservations
  // ---------------------------------------------------------
  const load = () => {
    api
      .reservations(branchId)
      .then((r) => setList(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    setList(null);

    load();

    if (activeBranch) {
      api
        .tables(activeBranch)
        .then((r) => setTables(r.data))
        .catch(() => setTables([]));
    }
  }, [branchId, activeBranch]);

  // ---------------------------------------------------------
  // Open new reservation
  // ---------------------------------------------------------
  const openNewReservation = () => {
    setForm({
      customer_name: "",
      phone: "",
      date: filterDate,
      time: "20:00",
      guests: 2,
      table_id: "",
      special_request: "",
    });

    setDateOpen(false);

    setOpen(true);
  };

  // ---------------------------------------------------------
  // Create reservation
  // ---------------------------------------------------------
  const create = async () => {
    if (!form.customer_name.trim()) {
      toast.error("Customer name is required");
      return;
    }

    if (!form.phone.trim()) {
      toast.error("Phone is required");
      return;
    }

    if (!/^\d{10}$/.test(form.phone)) {
      toast.error("Valid 10-digit mobile number is required");
      return;
    }

    if (!form.date) {
      toast.error("Date is required");
      return;
    }

    if (!form.time) {
      toast.error("Time is required");
      return;
    }

    if (Number(form.guests) < 1) {
      toast.error("Guests must be at least 1");
      return;
    }

    if (!form.table_id) {
      toast.error("Please select a table");
      return;
    }

    if (!activeBranch) {
      toast.error("Please select a branch");
      return;
    }

    try {
      await api.createReservation({
        ...form,
        branch_id: activeBranch,
        guests: Number(form.guests),
      });

      toast.success("Reservation added");

      setOpen(false);

      setForm({
        customer_name: "",
        phone: "",
        date: filterDate,
        time: "20:00",
        guests: 2,
        special_request: "",
        table_id: "",
      });

      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create reservation");
    }
  };

  // ---------------------------------------------------------
  // Check reservation time
  // ---------------------------------------------------------
  const isReservationTimeReached = (reservation) => {
    const now = new Date();

    const [year, month, day] = reservation.date.split("-").map(Number);

    const [hours, minutes] = reservation.time.split(":").map(Number);

    const reservationTime = new Date(
      year,
      month - 1,
      day,
      hours,
      minutes,
      0,
      0,
    );

    return now >= reservationTime;
  };

  // ---------------------------------------------------------
  // Update reservation status
  // ---------------------------------------------------------
  const advance = async (reservation, next) => {
    if (!next) return;

    if (
      (next === "seated" || next === "no_show") &&
      !isReservationTimeReached(reservation)
    ) {
      toast.error("This action is available only after the reservation time");

      return;
    }

    try {
      await api.setReservationStatus(reservation.id, next);

      toast.success(`Reservation marked ${next.replace("_", " ")}`);

      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update reservation");
    }
  };

  // ---------------------------------------------------------
  // Open confirmation dialog
  // ---------------------------------------------------------
  const requestStatusChange = (reservation, action) => {
    if (action.status === "cancelled" || action.status === "no_show") {
      setConfirmReservation({
        reservation,
        action,
      });

      return;
    }

    advance(reservation, action.status);
  };

  // ---------------------------------------------------------
  // Confirm status change
  // ---------------------------------------------------------
  const confirmStatusChange = async () => {
    if (!confirmReservation?.reservation) {
      return;
    }

    const { reservation, action } = confirmReservation;

    try {
      setConfirmLoading(true);

      await api.setReservationStatus(reservation.id, action.status);

      toast.success(`Reservation marked ${action.status.replace("_", " ")}`);

      setConfirmReservation(null);

      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update reservation");
    } finally {
      setConfirmLoading(false);
    }
  };

  // ---------------------------------------------------------
  // Loading
  // ---------------------------------------------------------
  if (!list) {
    return (
      <div className="p-6">
        <Loader label="Loading reservations" />
      </div>
    );
  }

  const filteredReservations = list.filter(
    (reservation) => reservation.date === filterDate,
  );

  return (
    <div className="p-4 sm:p-6 animate-fade-up">
      {/* -----------------------------------------------------
          Page Header
      ----------------------------------------------------- */}
      <PageHeader
        title="Reservations"
        subtitle="Table bookings"
        actions={
          <Button
            onClick={openNewReservation}
            className="rounded-xl font-semibold"
            data-testid="res-add"
          >
            <Plus size={16} className="mr-1" />
            New Reservation
          </Button>
        }
      />

      {/* -----------------------------------------------------
          Date Filter
      ----------------------------------------------------- */}
      <div className="mb-5 flex justify-end">
        <Input
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          className="h-10 w-auto rounded-xl"
          aria-label="Filter reservations by date"
        />
      </div>

      {/* -----------------------------------------------------
          Reservation List
      ----------------------------------------------------- */}
      {filteredReservations.length === 0 ? (
        <EmptyState
          icon={CalendarClock}
          title="No reservations for this date"
          subtitle="There are no table bookings scheduled for the selected date."
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredReservations.map((reservation) => (
            <div
              key={reservation.id}
              className="rounded-2xl bg-card border border-border p-5 hover:shadow-[0_8px_24px_rgba(28,25,23,0.06)] transition-shadow"
              data-testid={`res-${reservation.id}`}
            >
              {/* -------------------------------------------------
                    Customer + Status
                ------------------------------------------------- */}
              <div className="flex items-center justify-between">
                <p className="font-semibold">{reservation.customer_name}</p>

                <StatusBadge status={reservation.status} />
              </div>

              {/* Phone */}
              <p className="text-sm text-muted-foreground mt-1">
                {reservation.phone}
              </p>

              {/* -------------------------------------------------
                    Reservation Info
                ------------------------------------------------- */}
              <div className="flex flex-wrap items-center gap-3 mt-3 text-sm">
                <span className="font-semibold">{reservation.date}</span>

                <span className="text-muted-foreground">
                  {formatReservationTime(reservation.time)}
                </span>

                <span className="flex items-center gap-1 text-muted-foreground">
                  <Users size={13} />

                  {reservation.guests}
                </span>

                {reservation.table_name && (
                  <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium">
                    {reservation.table_name}
                  </span>
                )}
              </div>

              {/* -------------------------------------------------
                    Special Request
                ------------------------------------------------- */}
              {reservation.special_request && (
                <p className="text-xs text-muted-foreground mt-2 italic">
                  "{reservation.special_request}"
                </p>
              )}

              {/* -------------------------------------------------
                    Actions
                ------------------------------------------------- */}
              {ACTIONS[reservation.status]?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {ACTIONS[reservation.status].map((action) => {
                    const timeReached = isReservationTimeReached(reservation);

                    // Seated / No Show can only
                    // happen after reservation time.
                    if (
                      (action.status === "seated" ||
                        action.status === "no_show") &&
                      !timeReached
                    ) {
                      return null;
                    }

                    return (
                      <Button
                        key={action.status}
                        size="sm"
                        variant="outline"
                        className={cn(
                          "rounded-lg",

                          action.status === "confirmed" &&
                            "border-blue-200 text-blue-700 hover:bg-blue-50",

                          action.status === "seated" &&
                            "border-emerald-200 text-emerald-700 hover:bg-emerald-50",

                          action.status === "completed" &&
                            "border-emerald-200 text-emerald-700 hover:bg-emerald-50",

                          action.status === "cancelled" &&
                            "border-red-200 text-red-700 hover:bg-red-50",

                          action.status === "no_show" &&
                            "border-red-200 text-red-700 hover:bg-red-50",
                        )}
                        onClick={() => requestStatusChange(reservation, action)}
                        data-testid={`res-${action.status}-${reservation.id}`}
                      >
                        {action.label}
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ---------------------------------------------------------
          New Reservation Dialog
      --------------------------------------------------------- */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white sm:max-w-md"
          data-testid="res-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display">New Reservation</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {/* Customer Name */}
            <Input
              placeholder="Customer name"
              value={form.customer_name}
              onChange={(e) =>
                setForm({
                  ...form,
                  customer_name: e.target.value,
                })
              }
              className="h-11 rounded-xl"
              data-testid="res-name"
            />

            {/* Phone */}
            <Input
              placeholder="Phone"
              value={form.phone}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 10);

                setForm({
                  ...form,
                  phone: value,
                });
              }}
              type="tel"
              inputMode="numeric"
              maxLength={10}
              className="h-11 rounded-xl"
              data-testid="res-phone"
            />

            {/* Date / Time / Guests */}
            <div className="grid grid-cols-[1.4fr_1fr_0.7fr] gap-2">
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="h-11 w-full min-w-0 rounded-xl justify-start text-left font-normal"
                    data-testid="res-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />

                    {form.date ? (
                      <span className="truncate">{form.date}</span>
                    ) : (
                      <span className="text-muted-foreground">Select date</span>
                    )}
                  </Button>
                </PopoverTrigger>

                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={
                      form.date ? new Date(`${form.date}T00:00:00`) : undefined
                    }
                    onSelect={(date) => {
                      if (!date) return;

                      const year = date.getFullYear();

                      const month = String(date.getMonth() + 1).padStart(
                        2,
                        "0",
                      );

                      const day = String(date.getDate()).padStart(2, "0");

                      const formatted = `${year}-${month}-${day}`;

                      setForm({
                        ...form,
                        date: formatted,
                      });

                      setDateOpen(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {/* Time */}
              <Input
                type="time"
                value={form.time}
                onChange={(e) =>
                  setForm({
                    ...form,
                    time: e.target.value,
                  })
                }
                className="h-11 rounded-xl"
                data-testid="res-time"
              />

              {/* Guests */}
              <Input
                type="number"
                min="1"
                value={form.guests}
                onChange={(e) => {
                  const guests = e.target.value;

                  setForm((prev) => {
                    const selectedTable = tables.find(
                      (table) => table.id === prev.table_id,
                    );

                    const selectedTableStillValid =
                      selectedTable &&
                      isTableSelectableForReservation(
                        selectedTable,
                        prev.date,
                        prev.time,
                      ) &&
                      selectedTable.seats >= Number(guests);

                    return {
                      ...prev,
                      guests,
                      table_id: selectedTableStillValid ? prev.table_id : "",
                    };
                  });
                }}
                className="h-11 rounded-xl"
                placeholder="Guests"
                data-testid="res-guests"
              />
            </div>

            {/* Table */}
            <div className="space-y-1.5">
              <select
                value={form.table_id}
                onChange={(e) =>
                  setForm({
                    ...form,
                    table_id: e.target.value,
                  })
                }
                className="h-11 w-full rounded-xl border border-input bg-background px-3 text-sm"
                data-testid="res-table"
              >
                <option value="">Select table</option>

                {tables
                  .filter(
                    (table) =>
                      isTableSelectableForReservation(
                        table,
                        form.date,
                        form.time,
                      ) && table.seats >= Number(form.guests),
                  )
                  .map((table) => (
                    <option key={table.id} value={table.id}>
                      {table.name} — {table.seats} seats
                    </option>
                  ))}
              </select>

              {tables.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No tables available.
                </p>
              )}
            </div>

            {/* Special Request */}
            <Input
              placeholder="Special request (optional)"
              value={form.special_request}
              onChange={(e) =>
                setForm({
                  ...form,
                  special_request: e.target.value,
                })
              }
              className="h-11 rounded-xl"
              data-testid="res-special-request"
            />
          </div>

          {/* Footer */}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="h-11 rounded-xl"
            >
              Cancel
            </Button>

            <Button
              onClick={create}
              className="h-11 rounded-xl font-semibold"
              data-testid="res-save"
            >
              Save Reservation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------------------------------------------------
          Common Confirmation Dialog
      --------------------------------------------------------- */}
      <ConfirmDialog
        open={!!confirmReservation}
        title={
          confirmReservation?.action?.status === "no_show"
            ? "Mark as No Show?"
            : "Cancel Reservation?"
        }
        message={
          confirmReservation?.action?.status === "no_show"
            ? "Are you sure you want to mark this reservation as no show?"
            : "Are you sure you want to mark this reservation as cancelled?"
        }
        confirmText={
          confirmReservation?.action?.status === "no_show"
            ? "Mark No Show"
            : "Cancel Reservation"
        }
        cancelText="Keep Reservation"
        onConfirm={confirmStatusChange}
        onCancel={() => {
          if (!confirmLoading) {
            setConfirmReservation(null);
          }
        }}
        loading={confirmLoading}
        danger={true}
      />
    </div>
  );
}
